import React, { useEffect, useState } from "react";
import { api } from "@/lib/api";
import DocViewer from "@/components/DocViewer";
import { AlertTriangle, CheckCircle2, Database, FlaskConical } from "lucide-react";

const tabCls = (active) =>
  `rounded-lg px-3 py-1.5 text-xs font-semibold transition-colors ${
    active ? "bg-vino-700 text-white" : "bg-white/10 text-slate-200 hover:bg-white/20"
  }`;

export default function PlantillaPreview({ p, onClose }) {
  const [modo, setModo] = useState("plantilla"); // plantilla | prueba
  const [expedienteId, setExpedienteId] = useState("");
  const [expedientes, setExpedientes] = useState([]);
  const [meta, setMeta] = useState(null);

  useEffect(() => {
    api.get("/expedientes", { params: { estado: "validado" } })
      .then((r) => setExpedientes(r.data))
      .catch(() => setExpedientes([]));
  }, []);

  useEffect(() => {
    setMeta(null);
    api.get(`/plantillas/${p.key}/preview-meta`, { params: { expediente_id: expedienteId } })
      .then((r) => setMeta(r.data))
      .catch(() => setMeta(null));
  }, [p.key, expedienteId]);

  const url = `/plantillas/${p.key}/preview?modo=${modo}${modo === "prueba" && expedienteId ? `&expediente_id=${expedienteId}` : ""}`;
  const nombreArchivo = `${modo === "prueba" ? "PRUEBA" : "PLANTILLA"}-${p.codigo || p.key}.pdf`;

  return (
    <DocViewer
      key={url}
      url={url}
      titulo={p.nombre}
      subtitulo={`${p.codigo} · versión ${p.version} · ${p.estado === "aprobado" ? "Aprobada" : "Borrador"}`}
      nombreArchivo={nombreArchivo}
      onClose={onClose}
    >
      <div className="flex flex-wrap items-center gap-2">
        <span className={`inline-flex items-center gap-1 rounded-full px-2 py-0.5 text-[11px] font-semibold ${p.estado === "aprobado" ? "bg-emerald-500/20 text-emerald-300" : "bg-amber-500/20 text-amber-300"}`} data-testid="preview-estado">
          {p.estado === "aprobado" ? <CheckCircle2 className="h-3 w-3" /> : <AlertTriangle className="h-3 w-3" />}
          {p.estado === "aprobado" ? "Aprobada" : "Borrador"} · v{p.version}
        </span>
        <div className="flex gap-1.5" role="tablist">
          <button onClick={() => setModo("plantilla")} className={tabCls(modo === "plantilla")} data-testid="modo-plantilla">Ver plantilla</button>
          <button onClick={() => setModo("prueba")} className={tabCls(modo === "prueba")} data-testid="modo-prueba">Probar con datos</button>
        </div>
        {modo === "prueba" && (
          <select
            value={expedienteId}
            onChange={(e) => setExpedienteId(e.target.value)}
            data-testid="preview-expediente"
            className="rounded-lg border border-white/20 bg-slate-800 px-2.5 py-1.5 text-xs text-slate-100 outline-none"
          >
            <option value="">Datos ficticios (demostración)</option>
            {expedientes.map((e) => (
              <option key={e.id} value={e.id}>{e.nombre} · {e.numero_documento}</option>
            ))}
          </select>
        )}
      </div>
      <div className="mt-2 flex flex-wrap items-center gap-x-4 gap-y-1 text-[11px] text-slate-300">
        {modo === "plantilla" ? (
          <span className="flex items-center gap-1"><Database className="h-3 w-3" /> Documento sin datos del empleado; los campos disponibles aparecen entre «».</span>
        ) : (
          <span className="flex items-center gap-1 text-amber-300"><FlaskConical className="h-3 w-3" /> BORRADOR / PRUEBA: no guarda el documento, no cambia la aprobación, no solicita firmas ni modifica el expediente.</span>
        )}
        {modo === "prueba" && meta && (
          meta.faltantes.length === 0
            ? <span className="text-emerald-300" data-testid="preview-completo">Todos los campos tienen datos ({meta.fuente === "demo" ? "demostración" : meta.expediente_nombre}).</span>
            : <span className="text-amber-300" data-testid="preview-faltantes">Faltan datos (no se inventan): {meta.faltantes.join(", ")}</span>
        )}
      </div>
    </DocViewer>
  );
}
